// Parent.js
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import TransducerSelection from './components/TransducerSelection';
import BatterySelection from './components/BatterySelection';
import ShuttleSelection from './components/ShuttleSelection';
import PoleSelection from './components/PoleSelection';
import Summary from './components/Summary';
import './FishFinderBuilder.css';
import { findProductByName } from './data/productData';

const Parent = () => {
  const location = useLocation();
  
  // State for current step
  const [currentStep, setCurrentStep] = useState(1);
  
  // Initialize currentStep from location state if available
  useEffect(() => {
    if (location.state && location.state.initialStep) {
      setCurrentStep(location.state.initialStep);
    }
  }, [location.state]);
  
  // State for screen selection (legacy - now redirects to /compare-screens)
  const [selectedScreen, setSelectedScreen] = useState(null);
  
  // State for transducer selection
  const [selectedLivescope, setSelectedLivescope] = useState(null);
  const [selectedOpenWater, setSelectedOpenWater] = useState(null);
  const [selectedIceFisher, setSelectedIceFisher] = useState(null);
  
  // State for other components
  const [battery, setBattery] = useState(null);
  const [shuttle, setShuttle] = useState(null);
  const [pole, setPole] = useState(null);

  // Helper function to ensure we always have the latest product data
  const updateWithLatestData = (productType, product, setterFunction) => {
    if (!product) return;
    
    // Find the latest data for this product
    const latestData = findProductByName(productType, product.name);
    
    // If found and different from current, update with latest data
    if (latestData && JSON.stringify(latestData) !== JSON.stringify(product)) {
      setterFunction(latestData);
    }
  };

  // Keep product data up-to-date
  useEffect(() => {
    updateWithLatestData('screen', selectedScreen, setSelectedScreen);
    updateWithLatestData('livescope', selectedLivescope, setSelectedLivescope);
    updateWithLatestData('openWater', selectedOpenWater, setSelectedOpenWater);
    updateWithLatestData('iceFisher', selectedIceFisher, setSelectedIceFisher);
    updateWithLatestData('battery', battery, setBattery);
    updateWithLatestData('shuttle', shuttle, setShuttle);
    updateWithLatestData('pole', pole, setPole);
  }, [selectedScreen, selectedLivescope, selectedOpenWater, selectedIceFisher, battery, shuttle, pole]);

  // Navigation functions
  const nextStep = () => {
    setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const jumpToStep = (step) => {
    setCurrentStep(step);
  };

  const resetAll = () => {
    setSelectedScreen(null);
    setSelectedLivescope(null);
    setSelectedOpenWater(null);
    setSelectedIceFisher(null);
    setBattery(null);
    setShuttle(null);
    setPole(null);
    setCurrentStep(1);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        // Redirect to the main screen comparison page
        window.location.href = '/compare-screens';
        return <div>Redirecting to screen comparison...</div>;
      case 2:
        return (
          <TransducerSelection
            selectedScreen={selectedScreen}
            selectedLivescope={selectedLivescope}
            setSelectedLivescope={setSelectedLivescope}
            selectedOpenWater={selectedOpenWater}
            setSelectedOpenWater={setSelectedOpenWater}
            selectedIceFisher={selectedIceFisher}
            setSelectedIceFisher={setSelectedIceFisher}
            prevStep={prevStep}
            nextStep={nextStep}
            resetAll={resetAll}
            jumpToStep={jumpToStep}
          />
        );
      case 3:
        return (
          <BatterySelection
            selectedScreen={selectedScreen}
            battery={battery}
            setBattery={setBattery}
            prevStep={prevStep}
            nextStep={nextStep}
            resetAll={resetAll}
            jumpToStep={jumpToStep}
          />
        );
      case 4:
        return (
          <ShuttleSelection
            selectedScreen={selectedScreen}
            shuttle={shuttle}
            setShuttle={setShuttle}
            prevStep={prevStep}
            nextStep={nextStep}
            resetAll={resetAll}
            jumpToStep={jumpToStep}
          />
        );
      case 5:
        return (
          <PoleSelection
            selectedScreen={selectedScreen}
            pole={pole}
            setPole={setPole}
            prevStep={prevStep}
            nextStep={nextStep}
            resetAll={resetAll}
            jumpToStep={jumpToStep}
          />
        );
      case 6:
        return (
          <Summary
            selectedScreen={selectedScreen}
            selectedLivescope={selectedLivescope}
            selectedOpenWater={selectedOpenWater}
            selectedIceFisher={selectedIceFisher}
            battery={battery}
            shuttle={shuttle}
            pole={pole}
            goBack={prevStep}
            restart={resetAll}
          />
        );
      default:
        return <div>Error: Unknown step</div>;
    }
  };

  return (
    <div className="fish-finder-container">
      {renderStep()}
    </div>
  );
};

export default Parent;
